import React, {FC} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {AnalyticsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {PulsatingColorDot} from '../../../../libs/shared/ui/src/components/utility/pulsatingColorDot';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {getCompanyHasAnalyticsLiveDashboard} from '../../../store/data/company/companySelectors';
import {AnalyticsReportsEnum} from '../../helpers/reportHelpers/reportTypes';
import {StyledAnalyticsNavigationTitleDiv} from './legacyReportNavigationLinks';
import {ReportNavigationLink} from './reportNavigationLink';

/*
 * Constants.
 */

const interactionId = 'analytics-real-time-reports';
const interactionProps = buildInteractionIdProps(interactionId);

const realTimeReportsLinks = [
  {
    type: AnalyticsReportsEnum.LIVE_DASHBOARD,
    name: <Trans id="analytics-live-dashboard-reports">Live dashboard</Trans>,
    renderIcon: () => (
      <UserThemeConsumer>
        {(theme) => <PulsatingColorDot size={8} color={theme.palette.green.shade40} />}
      </UserThemeConsumer>
    ),
  },
] as const;

/*
 * Props.
 */

interface ReportNavigationRealTimeReportsLinksProps {
  isDisabled: boolean;
  panelId: AnalyticsPanelId;
}

/*
 * Styles.
 */

const StyledNavigationHelpWrapper = styled.div`
  display: flex;
  flex-direction: column;

  margin-bottom: 18px;

  &:first-child {
    margin-top: 4px;
  }
`;

/*
 * Component.
 */

export const ReportNavigationRealTimeReportsLinks: FC<ReportNavigationRealTimeReportsLinksProps> = ({
  isDisabled,
  panelId,
}) => {
  const hasLiveDashboardFeature = useAppSelector(getCompanyHasAnalyticsLiveDashboard);

  if (!hasLiveDashboardFeature) {
    return null;
  }

  return (
    <StyledNavigationHelpWrapper {...interactionProps}>
      <StyledAnalyticsNavigationTitleDiv>
        <Trans id="analytics-real-time-navigation-title">Real-time</Trans>
      </StyledAnalyticsNavigationTitleDiv>
      {realTimeReportsLinks.map((link) => (
        <ReportNavigationLink
          panelId={panelId}
          key={link.type}
          reportType={link.type}
          renderIcon={link.renderIcon}
          isDisabled={isDisabled}
        >
          {link.name}
        </ReportNavigationLink>
      ))}
    </StyledNavigationHelpWrapper>
  );
};
